import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  MapPin,
  Package,
  Droplets,
  Activity,
  FileText,
  MessageCircle,
} from "lucide-react";
import { fetchOrderLines } from "../../services/api";

const BloodRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRequestDetails();
    // eslint-disable-next-line
  }, [id]);

  const loadRequestDetails = async () => {
    try {
      setLoading(true);
      const storedRequests = JSON.parse(
        localStorage.getItem("bloodRequests") || "[]"
      );
      const foundRequest = storedRequests.find(
        (req) => req.id === parseInt(id)
      );
      if (!foundRequest) throw new Error("Request not found");
      setRequest(foundRequest);
      const lines = await fetchOrderLines(parseInt(id));
      setOrderLines(lines);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (docStatus) => {
    switch (docStatus) {
      case "Drafted":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "Voided":
        return "bg-red-100 text-red-800 border-red-200";
      case "Cancelled":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const safeIdentifier = (val) => {
    if (!val) return "N/A";
    if (typeof val === "string" || typeof val === "number") return val;
    if (typeof val === "object" && val.identifier) return val.identifier;
    return JSON.stringify(val);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">Error</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate("/blood-requests")}
            className="mt-4 btn-primary"
          >
            Back to Requests
          </button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-gray-600">Request not found</p>
          <button
            onClick={() => navigate("/blood-requests")}
            className="mt-4 btn-primary"
          >
            Back to Requests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Blood Request Details
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Document #{safeIdentifier(request.documentNo)}
            </p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
              safeIdentifier(request.docStatus)
            )}`}
          >
            {safeIdentifier(request.docStatus)}
          </div>
        </div>
      </div>

      {/* Information Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Request Information */}
        <div className="card p-2 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-3">
            <FileText className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Request Information
            </h2>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Document No:</span>
              <span className="font-medium">
                #{safeIdentifier(request.documentNo)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Date Ordered:</span>
              <span className="font-medium">
                {request.dateOrdered
                  ? new Date(request.dateOrdered).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Chapter:</span>
              <span className="font-medium">
                {safeIdentifier(request.organization)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              {/* <span className="text-gray-600">Warehouse:</span>
              <span className="font-medium">
                {safeIdentifier(request.warehouse)}
              </span> */}
            </div>
          </div>
        </div>

        {/* Patient Information */}
        <div className="card p-2 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-3">
            <User className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Patient Information
            </h2>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">
                {safeIdentifier(request.patientName)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Age:</span>
              <span className="font-medium">{safeIdentifier(request.age)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Sex:</span>
              <span className="font-medium">{safeIdentifier(request.sex)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Mobile No:</span>
              <span className="font-medium">
                {safeIdentifier(request.mobileNo)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Patient ID:</span>
              <span className="font-medium">
                {safeIdentifier(request.patientId)}
              </span>
            </div>
          </div>
        </div>

        {/* Blood Information */}
        <div className="card p-2 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-3">
            <Droplets className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Blood Information
            </h2>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Blood Group:</span>
              <span className="font-medium">
                {safeIdentifier(request.bloodGroup)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">RH Factor:</span>
              <span className="font-medium">
                {safeIdentifier(request.rhFactor)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Cross Match Needed:</span>
              <span className="font-medium">
                {safeIdentifier(request.x_match)}
              </span>
            </div>
          </div>
        </div>

        {/* Hospital Information */}
        <div className="card p-2 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-3">
            <MapPin className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Hospital Information
            </h2>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Hospital:</span>
              <span className="font-medium">
                {safeIdentifier(request.hospital)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Ward:</span>
              <span className="font-medium">
                {safeIdentifier(request.ward)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Bed No:</span>
              <span className="font-medium">
                {safeIdentifier(request.bedNo)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Type of Urgency:</span>
              <span className="font-medium text-red-600">
                {safeIdentifier(request.typeOfUrgency)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      {/* {request.description && (
        <div className="card p-2 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-3">
            <Activity className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Additional Information
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-sm bg-gray-50 rounded p-4">
            {safeIdentifier(request.description)}
          </p>
        </div>
      )} */}

      {/* Comments */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-3">
          <MessageCircle className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Blood Bank Response
          </h2>
        </div>
        <p
          className={`leading-relaxed text-sm rounded p-4 font-medium ${
            request.Help ? "text-red-600 bg-red-50" : "text-gray-500 bg-gray-50"
          }`}
        >
          {request.Help ? safeIdentifier(request.Help) : "No Comments"}
        </p>
      </div>

      {/* Order Lines */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-3">
          <Package className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Product Lines</h2>
        </div>
        {orderLines.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No product lines found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orderLines.map((line, index) => (
                  <tr key={line.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.productName || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {line.quantity} {line.uom || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BloodRequestDetails;
